import React, { useEffect, useRef, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  Group,
  Loader,
  Modal,
  Paper,
  Stack,
  Text,
  TextInput,
  Title,
  TypographyStylesProvider,
  useMantineTheme,
} from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import { IconCircleCheck, IconExclamationCircle } from '@tabler/icons-react';
import DOMPurify from 'dompurify';
import SignatureCanvas from 'react-signature-canvas';

import { fillAthleteMergeFields } from '../../agreementMergeFields';
import { hasDocumentsToSign, isAgreementWaiverComplete, useCheckout } from '../../CheckoutContext';

// The universal acknowledgment copy calls out cancellation terms when the
// agreement carries a cancellation clause (spec Item 5).
const ACKNOWLEDGMENT_COPY = 'I have read and agree to the package agreement above.';
const ACKNOWLEDGMENT_COPY_WITH_CANCELLATION =
  'I have read and agree to the package agreement above, including its cancellation terms.';
const SIGNATURE_SAVED_COPY = "Your signature is saved with today's date.";
const SIGNATURE_PAD_HEIGHT = 100;

interface SignatureCopyInput {
  signsContract: boolean;
  signsWaiver: boolean;
  isDependent: boolean;
  isLinked: boolean;
  athleteName: string;
}

export function signatureCopy({
  signsContract,
  signsWaiver,
  isDependent,
  isLinked,
  athleteName,
}: SignatureCopyInput): { label: string; hint: string } {
  const accepted = signsContract
    ? `agree to the package agreement${signsWaiver ? ' and the liability waiver' : ''} above`
    : 'accept the liability waiver above';
  if (isDependent) {
    return {
      label: 'Signature — as parent/guardian',
      hint: `By signing, you ${accepted} on ${athleteName}'s behalf, as their parent or guardian. ${SIGNATURE_SAVED_COPY}`,
    };
  }
  if (isLinked) {
    return {
      label: 'Signature',
      hint: `By signing, you ${accepted}. ${athleteName} will sign their own liability waiver from their account.`,
    };
  }
  return { label: 'Signature', hint: `By signing, you ${accepted}. ${SIGNATURE_SAVED_COPY}` };
}

const ClauseText = ({ html }: { html: string }): JSX.Element => (
  <TypographyStylesProvider>
    <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} />
  </TypographyStylesProvider>
);

const AgreementClauses = (): JSX.Element | null => {
  const { form, agreement } = useCheckout();
  const [fullAgreementOpened, setFullAgreementOpened] = useState(false);
  const preview = agreement.preview;

  if (!preview?.hasContract) return null;

  const segments = preview.segments.map(segment =>
    fillAthleteMergeFields(segment, form.values.athleteInformation),
  );
  const hasCancellationClause = preview.segments.some(segment => /cancel/i.test(segment));

  return (
    <Stack spacing='sm'>
      <Group position='apart'>
        <Title order={4}>Package Agreement</Title>
        <Button
          variant='subtle'
          compact
          data-cy='agreementViewFull'
          onClick={() => setFullAgreementOpened(true)}
        >
          View full package agreement
        </Button>
      </Group>
      <Paper p='sm' shadow='sm' radius='lg' mah={400} sx={{ overflowY: 'auto' }}>
        {segments.map((segment, index) => (
          <React.Fragment key={index}>
            <ClauseText html={segment} />
            {index !== segments.length - 1 && (
              <TextInput
                data-cy={`agreementInitialsInput-${index}`}
                label='Initials'
                placeholder='Type your initials'
                maw={160}
                maxLength={10}
                mb='sm'
                {...form.getInputProps(`agreementWaiver.initials.${index}`)}
              />
            )}
          </React.Fragment>
        ))}
      </Paper>
      <Checkbox
        data-cy='agreementAcknowledgeCheckbox'
        label={hasCancellationClause ? ACKNOWLEDGMENT_COPY_WITH_CANCELLATION : ACKNOWLEDGMENT_COPY}
        {...form.getInputProps('agreementWaiver.acknowledged', { type: 'checkbox' })}
      />
      <Modal
        opened={fullAgreementOpened}
        onClose={() => setFullAgreementOpened(false)}
        title={<Title order={4}>Package Agreement</Title>}
        size='xl'
      >
        {/* Read-only render: typed initials appear inline where the markers sit. */}
        {segments.map((segment, index) => (
          <React.Fragment key={index}>
            <ClauseText html={segment} />
            {index !== segments.length - 1 && (
              <Text fs='italic' fw='bold'>
                {(form.values.agreementWaiver.initials[index] ?? '').trim() || '____'}
              </Text>
            )}
          </React.Fragment>
        ))}
      </Modal>
    </Stack>
  );
};

/**
 * The signature canvas, drawn at the full width of its container. Resizing the
 * canvas wipes what was drawn on it, so a signature taken at another width is dropped
 * from the form and the buyer signs again.
 */
const SignaturePad = (): JSX.Element => {
  const theme = useMantineTheme();
  const { form } = useCheckout();
  const sigRef = useRef<SignatureCanvas>(null);
  const signedAtWidthRef = useRef<number | null>(null);
  const { ref: containerRef, width } = useElementSize();
  const canvasWidth = Math.floor(width);

  useEffect(() => {
    if (signedAtWidthRef.current === null || signedAtWidthRef.current === canvasWidth) return;
    signedAtWidthRef.current = null;
    form.setFieldValue('agreementWaiver.signature', null);
  }, [canvasWidth, form]);

  const handleSignatureEnd = (): void => {
    signedAtWidthRef.current = canvasWidth;
    form.setFieldValue('agreementWaiver.signature', sigRef.current?.toDataURL() ?? null);
  };

  const handleClearSignature = (): void => {
    sigRef.current?.clear();
    signedAtWidthRef.current = null;
    form.setFieldValue('agreementWaiver.signature', null);
  };

  return (
    <>
      <Box ref={containerRef} data-cy='agreementSignature'>
        {canvasWidth > 0 && (
          <SignatureCanvas
            key={canvasWidth}
            canvasProps={{ width: canvasWidth, height: SIGNATURE_PAD_HEIGHT, style: { display: 'block' } }}
            backgroundColor={theme.colors.gray[2]}
            ref={sigRef}
            onEnd={handleSignatureEnd}
          />
        )}
      </Box>
      <Group>
        <Button
          variant='outline'
          color='red'
          compact
          data-cy='agreementSignatureClear'
          disabled={!form.values.agreementWaiver.signature}
          onClick={handleClearSignature}
        >
          Clear signature
        </Button>
      </Group>
    </>
  );
};

const WaiverSection = (): JSX.Element | null => {
  const theme = useMantineTheme();
  const { form, agreement } = useCheckout();
  const preview = agreement.preview;

  if (!preview?.waiverRequired) return null;

  if (agreement.waiverDeferred) {
    return (
      <Stack spacing='xs' data-cy='agreementWaiverDeferred'>
        <Title order={4}>Waiver</Title>
        <Text c='dimmed' size='sm'>
          Linked profiles sign their own waiver. It will be waiting in their account when they activate it.
        </Text>
      </Stack>
    );
  }

  if (agreement.waiverOnFile) {
    return (
      <Group spacing='xs' data-cy='agreementWaiverOnFile'>
        <IconCircleCheck size={20} color={theme.colors.green[6]} />
        <Text c='dimmed' size='sm'>
          A signed waiver is already on file.
        </Text>
      </Group>
    );
  }

  return (
    <Stack spacing='xs'>
      <Title order={4}>Waiver</Title>
      {preview.waivers.map((waiver, index) => (
        <Paper
          key={index}
          p='sm'
          shadow='sm'
          radius='lg'
          mah={400}
          sx={{ overflowY: 'auto' }}
          data-cy='agreementWaiverText'
        >
          <Text fw={600} mb='xs'>
            {waiver.description}
          </Text>
          <ClauseText html={fillAthleteMergeFields(waiver.contents, form.values.athleteInformation)} />
        </Paper>
      ))}
    </Stack>
  );
};

const SignatureSection = (): JSX.Element | null => {
  const { form, agreement } = useCheckout();
  const preview = agreement.preview;

  if (!hasDocumentsToSign(agreement)) return null;

  const { whoIsThisFor, athleteInformation } = form.values;
  const { label, hint } = signatureCopy({
    signsContract: !!preview?.hasContract,
    signsWaiver: agreement.waiverNeeded,
    isDependent:
      whoIsThisFor === 'newDependentAccount' || (whoIsThisFor === 'subAccount' && !agreement.waiverDeferred),
    isLinked: !!preview?.waiverRequired && agreement.waiverDeferred,
    athleteName: `${athleteInformation.firstName} ${athleteInformation.lastName}`.trim(),
  });

  return (
    <>
      <Divider />
      <Stack spacing='xs'>
        <Title order={4}>{label}</Title>
        <Text c='dimmed' size='sm' data-cy='agreementSignatureHint'>
          {hint}
        </Text>
        <SignaturePad />
      </Stack>
    </>
  );
};

const AgreementWaiver = (): JSX.Element => {
  const { form, agreement, completeStep } = useCheckout();

  if (agreement.isLoading) {
    return (
      <Group position='center' p='xl'>
        <Loader />
        <Text c='dimmed'>Loading the package agreement…</Text>
      </Group>
    );
  }

  // A failed load blocks the step (and therefore the purchase) rather than silently
  // skipping a legally required agreement.
  if (agreement.isError || !agreement.preview) {
    return (
      <Alert
        icon={<IconExclamationCircle size='md' />}
        title='The agreement could not be loaded'
        color='red'
        data-cy='agreementLoadError'
      >
        <Stack align='flex-start' spacing='sm'>
          <Text size='sm'>The purchase cannot continue until the agreement loads.</Text>
          <Button variant='outline' color='red' compact onClick={() => agreement.refetch()}>
            Retry
          </Button>
        </Stack>
      </Alert>
    );
  }

  const canContinue = isAgreementWaiverComplete(agreement, form.values.agreementWaiver);

  return (
    <Stack>
      <AgreementClauses />
      {agreement.preview.hasContract && agreement.preview.waiverRequired && <Divider />}
      <WaiverSection />
      <SignatureSection />
      <Group position='right'>
        <Button
          data-cy='agreementContinue'
          disabled={!canContinue}
          onClick={() => completeStep('agreementWaiver')}
        >
          Continue
        </Button>
      </Group>
    </Stack>
  );
};

export default AgreementWaiver;
